import { Component, OnInit, ViewChild } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EquipeVendaService } from '@app/modules/equipe-venda/services';
import { IParceiro } from '@app/modules/parceiro/model';
import { ParceiroService } from '@app/modules/parceiro/services/parceiro.service';
import { IVendedor } from '@app/modules/vendedor/model/vendedor.model';
import { VendedorService } from '@app/modules/vendedor/services';
import { Utils } from '@app/shared/helpers/utils';
import { of, zip } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import {
  CustomValidators,
  IRoute,
  Pattern,
  TokenService,
  UserService
} from 'viggo-sistemas-core-web';
import {
  validateCnpj,
  validateCpf
} from '../../../../shared/utils/validators_cpf_cnpj';
import { FastContatoComponent } from '../fast-contato/fast-contato.component';
import { FastEnderecoComponent } from '../fast-endereco/fast-endereco.component';

@Component({
  selector: 'app-fast-vendedor',
  templateUrl: './fast-vendedor.component.html',
  styleUrls: ['./fast-vendedor.component.scss'],
  providers: [UserService, ParceiroService, EquipeVendaService, VendedorService]
})
export class FastVendedorComponent implements OnInit {
  @ViewChild(FastEnderecoComponent)
  public _fastEndereco: FastEnderecoComponent;

  @ViewChild(FastContatoComponent)
  public _fastContato: FastContatoComponent;

  form: UntypedFormGroup;
  user_id: string;
  findParceiro = false;

  messages = {
    nome_razao_social: {
      required: 'O nome é requerido.',
      maxlength: 'O nome deve ter no maximo 60 caracteres.',
      minlength: 'O nome é requerido.',
      isEmptyValue: 'Valor inválido: remova os espaços!'
    },
    apelido_nome_fantasia: {
      maxlength: 'Este nome é muito longo.',
      minlength: 'Este nome é muito curto.',
      isEmptyValue: 'Valor inválido: remova os espaços!'
    },
    cpf_cnpj: {
      repeated: 'Informação já cadastrada.',
      mask: 'Valor inválido.',
      required: 'O CPF/CNPJ é obrigatório',
      pattern: 'Este CPF/CNPJ é inválido.',
      maxlength: 'O CPF/CNPJ deve conter no máximo 14 caracteres.',
      duplicate: 'O CPF/CNPJ já esta cadastrado',
      validateCPF: 'Este CPF é inválido.',
      validateCNPJ: 'Este CNPJ é inválido.'
    },
    rg_insc_est: {
      pattern: 'Este valor é inválido.',
      maxlength: 'O campo deve conter no máximo 20 caracteres.',
      existSpecialChar: 'Valor inválido: não aceita pontuação!',
      isEmptyValue: 'Valor inválido: remova os espaços!'
    }
  };

  viewInit = false;

  entity: IVendedor = {
    parceiro: {
      contatos: [],
      enderecos: []
    } as IParceiro
  } as IVendedor;

  constructor(
    public dialogRef: MatDialogRef<FastVendedorComponent>,
    public vendedorService: VendedorService,
    protected tokenService: TokenService,
    private parceiroService: ParceiroService,
    public userService: UserService,
    public evendaService: EquipeVendaService,
    public snackBar: MatSnackBar,
    private fb: UntypedFormBuilder
  ) {}

  ngOnInit() {
    const today = new Date();
    this.form = this.fb.group({
      nome_razao_social: [
        this.entity.parceiro.nome_razao_social,
        [
          Validators.required,
          Validators.maxLength(60),
          Validators.minLength(1),
          CustomValidators.isEmptyValue()
        ]
      ],
      apelido_nome_fantasia: [
        this.entity.parceiro.apelido_nome_fantasia,
        [
          Validators.minLength(1),
          Validators.maxLength(60),
          CustomValidators.isEmptyValue()
        ]
      ],
      cpf_cnpj: [this.entity.parceiro.cpf_cnpj, [Validators.required]],
      rg_insc_est: [
        this.entity.parceiro.rg_insc_est,
        [
          Validators.pattern(Pattern.patternInscEstadual()),
          Validators.maxLength(20),
          CustomValidators.existSpecialChar(),
          CustomValidators.isEmptyValue()
        ]
      ],
      data_nascimento: [
        this.entity.data_nascimento,
        CustomValidators.date(today)
      ],
      user: [
        null,
        [CustomValidators.typeIs('object'), CustomValidators.isEmptyValue()]
      ],
      equipe_venda_id: [
        null,
        [CustomValidators.typeIs('object'), CustomValidators.isEmptyValue()]
      ],
      padrao_catalogo: [false]
    });
    this.user_id = this.form.value.user ? this.form.value.user.id : undefined;
    this.form.controls.cpf_cnpj.valueChanges.subscribe(() => {
      this.validar();
    });
  }

  validar() {
    let cpfCnpjValue = this.form.controls.cpf_cnpj.value;
    if (cpfCnpjValue.toString().length <= 11) {
      if (validateCpf(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCPF: true });
        this.form.updateValueAndValidity();
      }
    } else {
      if (validateCnpj(cpfCnpjValue)) {
        this.form.controls.cpf_cnpj.setErrors({ validateCNPJ: true });
        this.form.updateValueAndValidity();
      }
    }
  }

  public setInformacoesParceiro(
    parceiro: IParceiro = { contatos: [], enderecos: [] } as IParceiro
  ): void {
    this.entity.parceiro = parceiro;
    this._fastEndereco.updateFormValue(parceiro.enderecos);
    this._fastContato.updateValues(parceiro);
  }

  get formValid(): boolean {
    return this.viewInit && this._fastEndereco.form.valid && this.form.valid;
  }

  get formAddressValid(): boolean {
    if (this.viewInit && this._fastEndereco.form.invalid)
      this._fastEndereco.form.markAllAsTouched();
    return this.viewInit && this._fastEndereco.form.invalid;
  }

  get formDadosValid() {
    if (this.form.invalid) this.form.markAllAsTouched();
    return this.viewInit && this.form.invalid;
  }

  notText(event) {
    if (
      (!/^\d+$/.test(event.key) || /[`,.]/.test(event.key)) &&
      event.key !== 'Backspace' &&
      event.key !== 'Delete' &&
      event.key !== 'Tab' &&
      event.key !== 'ArrowLeft' &&
      event.key !== 'ArrowRight' &&
      !event.ctrlKey
    )
      return false;
  }

  public checkRgInscEstadual(): void {
    const rgInsc = this.form.controls.rg_insc_est;

    if (
      rgInsc.value === null ||
      rgInsc.invalid ||
      rgInsc.value === '' ||
      this.findParceiro
    )
      return;
    const rgInscValue: string = rgInsc.value.replace(/[^a-zA-Z0-9 ]/g, '');

    this.parceiroService
      .getEntitiesByParams(
        `rg_insc_est=${rgInscValue}`,
        this.tokenService.getDomain().id
      )
      .subscribe((parceiro: IParceiro) => {
        if (parceiro[0] && parceiro[0].id) {
          this.form.controls.rg_insc_est.setErrors({ duplicate: true });
          this.form.updateValueAndValidity();
          return {} as IParceiro;
        }
      });
  }

  public checkCpfCnpj(): void {
    const cpfCnpj = this.form.controls.cpf_cnpj;

    if (cpfCnpj.invalid) return;
    const cpfCnpjValue: string = cpfCnpj.value;

    this.parceiroService
      .getEntitiesByParams(
        `cpf_cnpj=${cpfCnpjValue}`,
        this.tokenService.getDomain().id
      )
      .pipe(
        map(parceiros => {
          if (parceiros.length) {
            this.findParceiro = true;
            return { ...parceiros[0], cpf_cnpj: parceiros[0].cpf_cnpj.trim() };
          } else {
            this.findParceiro = false;
            return {} as IParceiro;
          }
        }),
        mergeMap(parceiro => {
          if (parceiro === undefined)
            return of(parceiro);
          return this.vendedorService.getEntity(parceiro.id).pipe(
            map(ent => {
              if (ent && ent.id) {
                this.form.controls.cpf_cnpj.setErrors({ duplicate: true });
                this.form.updateValueAndValidity();
                return {} as IParceiro;
              }
              return parceiro;
            }),
            catchError(() => of(parceiro))
          );
        })
      )
      .subscribe((parceiro: IParceiro) => {
        if (parceiro === undefined) return;
        else if (Object.keys(parceiro).length !== 0) {
          this.setInformacoesParceiro(parceiro);
          this.form.patchValue({
            apelido_nome_fantasia: parceiro.apelido_nome_fantasia || null,
            nome_razao_social: parceiro.nome_razao_social || null,
            rg_insc_est: parceiro.rg_insc_est || null,
            data_nascimento: null,
            user_id: null,
            equipe_venda_id: null
          });
        }
      });
  }

  onSubmit(): void {
    var entity: IVendedor | any = Utils.clearObject<IVendedor>({
      ...this.entity,
      parceiro: Utils.clearObject({
        ...this.entity.parceiro,
        ...this.form.value,
        data_nascimento: undefined,
        equipe_venda_id: undefined,
        user_id: undefined,
        enderecos: this._fastEndereco.getValues(),
        contatos: this._fastContato.getValues()
      }),
      domain_id: this.tokenService.getDomain().id,
      data_nascimento: this.form.value.data_nascimento
        ? this.form.value.data_nascimento
        : null,
      user_id: this.form.value.user ? this.form.value.user.id : null,
      equipe_venda_id: this.form.value.equipe_venda_id
        ? this.form.value.equipe_venda_id.id
        : null,
      carteira: []
    } as IVendedor);
    delete entity.equipe_venda;
    delete entity.parceiro.user;
    entity.parceiro = Utils.trimAttributes(entity.parceiro);
    entity.parceiro = Utils.clearEmptyString(entity.parceiro);
    this.insert(entity);
  }

  insert(entity: IVendedor): void {
    if (entity.parceiro.id) {
      zip(
        this.parceiroService.update({
          ...entity.parceiro
        }),
        this.vendedorService.insert({
          ...entity,
          id: entity.parceiro.id,
          parceiro: undefined
        })
      )
        .pipe(
          mergeMap(([, vendedor]) => {
            return this.vendedorService.getEntity(vendedor.id, [
              'parceiro',
              'carteira'
            ]);
          })
        )
        .subscribe(
          vendedor => {
            this.snackBar.open('Vendedor criado com sucesso.', 'X', {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['msg-success']
            });
            this.dialogRef.close(vendedor);
          },
          () => {
            this.snackBar.open('Ops, estamos com problemas.', 'X', {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['msg-error']
            });
          }
        );
    } else {
      this.parceiroService
        .insert({
          ...entity.parceiro,
          domain_id: this.tokenService.getDomain().id
        })
        .pipe(
          mergeMap(parceiro => {
            return this.vendedorService.insert({
              ...entity,
              id: parceiro.id,
              parceiro: undefined
            });
          }),
          mergeMap(vendedor => {
            return this.vendedorService.getEntity(vendedor.id, [
              'parceiro',
              'carteira'
            ]);
          })
        )
        .subscribe(
          vendedor => {
            this.snackBar.open('Vendedor criado com sucesso.', 'X', {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['msg-success']
            });
            this.dialogRef.close(vendedor);
          },
          () => {
            this.snackBar.open('Ops, estamos com problemas.', 'X', {
              duration: 2000,
              horizontalPosition: 'right',
              verticalPosition: 'top',
              panelClass: ['msg-error']
            });
          }
        );
    }
  }

  ngAfterViewInit(): void {
    this.viewInit = true;
  }

  /**
   * Valida as rotas do usuário
   * @param routes
   * @param url_name
   * @returns
   */
  verificarRotasDeAcesso(routes: IRoute[], url_name: string): IRoute[] {
    if (routes && routes.length > 0 && url_name) {
      return routes.filter(r => new RegExp(`^${url_name}`).test(r.url));
    }
  }

  /**
   * Valida se as rotas do usuário são equivalentes ao pacote de rotas do fiscal
   * @param routesAuthorization
   * @param action
   * @param params
   * @param url_name
   * @returns
   */
  validarAdicao(
    routesAuthorization: IRoute[],
    action: string,
    params = '',
    url_name: string
  ): boolean {
    if (routesAuthorization && routesAuthorization.length > 0) {
      const url_return = routesAuthorization.find(
        route => route.url === url_name && route.method === action
      );
      return url_return ? true : false;
    }
  }

  /**
   * CHECA SE TEM A ROTA  DE EQUIPE DE VENDAS
   * @returns
   */
  public temEquipe(): boolean {
    const action = 'GET' as string;
    const params = '' as string;
    const url_eqp = '/equipe_vendas' as string;

    var routesAuthorizationEquipe = this.verificarRotasDeAcesso(
      this.tokenService.getRoutes(),
      url_eqp
    ) as IRoute[];

    const temEquipe = this.validarAdicao(
      routesAuthorizationEquipe,
      action,
      params,
      url_eqp
    );

    return temEquipe;
  }

  /**
   * CHECA SE TEM A ROTA DE USUARIO
   * @returns
   */
  public temUsuario(): boolean {
    const action = 'GET' as string;
    const params = '' as string;
    const url_user = '/users/disponiveis' as string;

    var routesAuthorizationUsuario = this.verificarRotasDeAcesso(
      this.tokenService.getRoutes(),
      url_user
    ) as IRoute[];

    const temUsuario = this.validarAdicao(
      routesAuthorizationUsuario,
      action,
      params,
      url_user
    );

    return temUsuario;
  }
}
